import { useId, useState } from "react";
import {
  SUPPORTED_ACCOUNT_LOCALE_CODES,
  isAccountLocale,
} from "@kaleo/account-localization";
import { TARGET_CAPTION_LANGUAGES } from "@kaleo/captions-core/languages";
import { useExposedProps } from "zudoku/hooks";
import { fetchAppRequest } from "../lib/app-request";
import { replaceDocsLocationLocale } from "../lib/docs-routes";
import { APP_ORIGIN } from "../lib/localization";
import { useDocsLocalization } from "./use-docs-localization";

const localeOptions = SUPPORTED_ACCOUNT_LOCALE_CODES.map((code) => {
  const language = TARGET_CAPTION_LANGUAGES.find(
    (candidate) => candidate.code === code,
  );
  if (!language) {
    throw new Error(
      `No target-language entry found for docs locale "${code}".`,
    );
  }

  return {
    code,
    emoji: language.emoji,
    nativeName: language.nativeName,
  };
});

export function LocaleSelector({ compact = false }: { compact?: boolean }) {
  const id = useId();
  const { location } = useExposedProps();
  const { locale, messages } = useDocsLocalization();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const handleChange = async (value: string) => {
    if (!isAccountLocale(value) || value === locale || pending) return;

    setError("");
    setPending(true);
    try {
      const response = await fetchAppRequest(
        `${APP_ORIGIN}/api/preferences/locale`,
        {
          body: new URLSearchParams({ locale: value }),
          cache: "no-store",
          credentials: "include",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          method: "POST",
        },
      );
      const body = (await response.json().catch(() => null)) as {
        locale?: unknown;
      } | null;
      if (!response.ok || body?.locale !== value) {
        throw new Error(`locale_preference_failed:${response.status}`);
      }

      globalThis.location.assign(replaceDocsLocationLocale(location, value));
    } catch {
      setError(messages.saveLanguageError);
      setPending(false);
    }
  };

  return (
    <div
      className={`kaleo-locale-selector${compact ? " kaleo-locale-selector-compact" : ""}`}
    >
      <label className="kaleo-sr-only" htmlFor={id}>
        {messages.chooseLanguage}
      </label>
      <select
        aria-describedby={error ? `${id}-error` : undefined}
        aria-label={messages.chooseLanguage}
        disabled={pending}
        id={id}
        value={locale}
        onChange={(event) => void handleChange(event.target.value)}
      >
        {localeOptions.map((option) => (
          <option key={option.code} value={option.code}>
            {`${option.emoji} ${option.nativeName}`}
          </option>
        ))}
      </select>
      <svg
        aria-hidden="true"
        className="kaleo-locale-selector-chevron"
        viewBox="0 0 24 24"
      >
        <path d="m7 10 5 5 5-5" />
      </svg>
      {error ? (
        <span
          className="kaleo-locale-selector-error"
          id={`${id}-error`}
          role="alert"
        >
          {error}
        </span>
      ) : null}
    </div>
  );
}
